import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useMyReadings, useConfirmReading } from '@/api/documents';
import { useCategoryLabel } from '@/api/categories';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { EmptyState } from '@/components/shared/empty-state';
import { Pagination } from '@/components/shared/pagination';
import { useDocumentViewer } from '@/components/shared/use-document-viewer';
import { ReadingCard } from './_components/reading-card';
import { ReadingsFilters } from './_components/readings-filters';
import { useReadingFilters } from './_components/use-reading-filters';
import type { ReadingAssignment } from '@/types';

export function ReadingsPage() {
  const categoryLabel = useCategoryLabel('document_category');
  const filters = useReadingFilters();
  const { openDocument, viewer } = useDocumentViewer(); // #28/#23/#30 visor inline
  // #27 — documents opened in this session, so "Confirmar" enables immediately
  // without waiting for the reading list to refetch its opened_at.
  const [openedIds, setOpenedIds] = useState<Set<number>>(() => new Set());

  const {
    data: readingsRes,
    isLoading,
    error,
  } = useMyReadings({
    status: filters.status,
    search: filters.search || undefined,
    page: filters.page,
    per_page: 10,
  });

  const confirmReading = useConfirmReading();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoConfirmRef = useRef(false);

  const readings = readingsRes?.data ?? [];
  const pagination = readingsRes?.meta?.pagination;

  // I4/I5/I7: a deep-link (?highlight=<documentId>) points at one specific reading.
  const highlightId = Number(searchParams.get('highlight')) || 0;
  const highlightRef = useRef<HTMLDivElement>(null);

  // #27 — require opening before confirming, unless there is nothing to open
  // (no file and no external link) — in which case gating would be a dead-end.
  const canConfirm = (r: ReadingAssignment) => {
    const openable = Boolean(r.document.file_url || r.document.external_url);
    return !openable || r.opened_at !== null || openedIds.has(r.document_id);
  };

  // Auto-confirm from an email link (?confirm=documentId). #27: only if the reader
  // has already opened the document; otherwise guide them to open it (highlight).
  useEffect(() => {
    const confirmId = searchParams.get('confirm');
    if (!confirmId || autoConfirmRef.current || confirmReading.isPending) return;
    const docId = Number(confirmId);
    if (isNaN(docId) || docId <= 0) return;
    const target = readings.find((r) => r.document_id === docId);
    if (!target) return; // wait until the readings load
    autoConfirmRef.current = true;
    // Solo se consumen los parámetros del deep-link; los filtros de la URL se conservan.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('confirm');
        if (canConfirm(target)) next.delete('highlight');
        else next.set('highlight', String(docId));
        return next;
      },
      { replace: true },
    );
    if (canConfirm(target)) confirmReading.mutate(docId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, readings, confirmReading.isPending]);

  useEffect(() => {
    if (highlightId && highlightRef.current) {
      highlightRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightId, readings.length]);

  function handleOpen(reading: ReadingAssignment) {
    setOpenedIds((prev) => new Set(prev).add(reading.document_id));
    openDocument(reading.document_id, reading.document.title);
  }

  return (
    <div className="space-y-6">
      {viewer}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Lecturas Asignadas</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Documentos que debes revisar y confirmar su lectura.
        </p>
      </div>

      <ReadingsFilters
        status={filters.status}
        searchInput={filters.searchInput}
        onStatusChange={filters.setStatus}
        onSearchChange={filters.setSearchInput}
      />

      {confirmReading.error && (
        <ErrorMessage message="No se pudo confirmar la lectura. Abre el documento e inténtalo de nuevo." />
      )}

      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message="No se pudieron cargar las lecturas asignadas." />
      ) : readings.length === 0 ? (
        filters.hasFilters ? (
          <EmptyState
            title="Sin resultados"
            description="No hay lecturas que coincidan con los filtros."
            action={
              <button
                type="button"
                onClick={filters.clear}
                className="min-h-[44px] rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              >
                Limpiar filtros
              </button>
            }
          />
        ) : (
          <EmptyState
            title="Sin lecturas"
            description="No tienes lecturas asignadas actualmente."
          />
        )
      ) : (
        <>
          <div className="space-y-3">
            {readings.map((reading) => (
              <ReadingCard
                key={reading.id}
                ref={highlightRef}
                reading={reading}
                categoryLabel={categoryLabel}
                highlighted={highlightId === reading.document_id}
                canConfirm={canConfirm(reading)}
                confirming={confirmReading.isPending}
                onOpen={() => handleOpen(reading)}
                onConfirm={() => confirmReading.mutate(reading.document_id)}
              />
            ))}
          </div>

          {pagination && <Pagination meta={pagination} onPageChange={filters.setPage} />}
        </>
      )}
    </div>
  );
}
